import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { NavigateFunction, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { getInvitationDetails } from "../../../service/publicquery";
import { Status } from "../../../service/Shared";
import { Toast } from "../../../widget";
import {
  ACCEPT_INVITATION_QUERY_PARAM_KEYS,
  ACCEPT_INVITATION_QUERY_PARAM_LABELS,
  ACCEPT_INVITATION_SHARED_QUERY_PARAM_KEYS,
} from "./constants/acceptInvitation";
import { AcceptInvitationSharedContext } from "./models/acceptInvitation";

interface UseAcceptInvitationReturnData {
  urlToken: string | undefined;
  developerName: string | null;
  accountCreated: boolean;
  email: string;
  firstName: string;
  hasSharedInvitationQueryParams: boolean;
  lastName: string;
  navigation: NavigateFunction;
  sharedInvitationContext: AcceptInvitationSharedContext | null;
  sharedInvitationQueryParamErrors: string[];
  setAccountCreated: Dispatch<SetStateAction<boolean>>;
}

type AcceptInvitationPageRouteParams = "token";

const getSharedInvitationQueryParamValue = (searchParams: URLSearchParams, queryParamKey: string): string => {
  return searchParams.get(queryParamKey)?.trim() ?? searchParams.get(`amp;${queryParamKey}`)?.trim() ?? "";
};

export const useAcceptInvitation = (): UseAcceptInvitationReturnData => {
  const { token } = useParams<AcceptInvitationPageRouteParams>();
  const navigation = useNavigate();
  const [searchParams] = useSearchParams();
  const [accountCreated, setAccountCreated] = useState(false);
  const [developerName, setDeveloperName] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const hasSharedInvitationQueryParams = useMemo(
    () =>
      ACCEPT_INVITATION_SHARED_QUERY_PARAM_KEYS.some(
        (queryParamKey) => searchParams.has(queryParamKey) || searchParams.has(`amp;${queryParamKey}`)
      ),
    [searchParams]
  );

  const sharedInvitationQueryParamErrors = useMemo(() => {
    if (!hasSharedInvitationQueryParams) {
      return [];
    }

    return ACCEPT_INVITATION_SHARED_QUERY_PARAM_KEYS.reduce<string[]>((errors, queryParamKey) => {
      if (!getSharedInvitationQueryParamValue(searchParams, queryParamKey)) {
        errors.push(`${ACCEPT_INVITATION_QUERY_PARAM_LABELS[queryParamKey]} is missing from this invitation link.`);
      }

      return errors;
    }, []);
  }, [hasSharedInvitationQueryParams, searchParams]);

  const sharedInvitationContext = useMemo((): AcceptInvitationSharedContext | null => {
    if (!hasSharedInvitationQueryParams || sharedInvitationQueryParamErrors.length > 0) {
      return null;
    }

    return {
      sourceOrganisationDisplayName: getSharedInvitationQueryParamValue(
        searchParams,
        ACCEPT_INVITATION_QUERY_PARAM_KEYS.sourceOrganisationDisplayName
      ),
      projectName: getSharedInvitationQueryParamValue(searchParams, ACCEPT_INVITATION_QUERY_PARAM_KEYS.projectName),
      senderFirstName: getSharedInvitationQueryParamValue(
        searchParams,
        ACCEPT_INVITATION_QUERY_PARAM_KEYS.senderFirstName
      ),
      senderLastName: getSharedInvitationQueryParamValue(
        searchParams,
        ACCEPT_INVITATION_QUERY_PARAM_KEYS.senderLastName
      ),
    };
  }, [hasSharedInvitationQueryParams, searchParams, sharedInvitationQueryParamErrors.length]);

  const fetchData = useCallback(async () => {
    if (token) {
      const res = await getInvitationDetails({
        token,
      });

      if (res.status === Status.Success && res.data) {
        setDeveloperName(res.data.targetOrganisation.displayName);
        setEmail(res.data.targetEmail);
        setFirstName(res.data.targetFirstName);
        setLastName(res.data.targetLastName);
      } else {
        Toast.error({ message: "Invalid invitation link" });
      }
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData, token]);

  return {
    urlToken: token,
    developerName,
    accountCreated,
    email,
    firstName,
    hasSharedInvitationQueryParams,
    lastName,
    navigation,
    sharedInvitationContext,
    sharedInvitationQueryParamErrors,
    setAccountCreated,
  };
};
